import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import {
  Badge as BadgeIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import redemptionService, { RedemptionStatus } from '../../services/redemptionService';

interface BusinessRedemptionIdentitySectionProps {
  onVerifyIdentity: () => void;
  isIdVerified: boolean;
  idVerificationStarting: boolean;
}

const BusinessRedemptionIdentitySection: React.FC<BusinessRedemptionIdentitySectionProps> = ({
  onVerifyIdentity,
  isIdVerified,
  idVerificationStarting,
}) => {
  const [status, setStatus] = useState<RedemptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const next = await redemptionService.getStatus();
      setStatus(next);
    } catch {
      setError('Could not load redemption identity status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">
          Loading redemption identity requirements…
        </Typography>
      </Box>
    );
  }

  if (!status?.isBusinessAccount) {
    return null;
  }

  const alias = status.eligibleTeamAlias;
  const nameBindingMet = status.nameBindingMet ?? status.verification.isNameBoundToVerifiedIdentity;
  const displayName = alias?.displayName?.trim() || '';
  const missingDisplayName = !displayName;

  return (
    <Box id="business-redemption-identity" sx={{ mt: 3 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack spacing={1.5}>
        <Typography variant="subtitle2">Cash Redemption — Authorized Withdrawer Identity</Typography>

        <Alert severity="info" icon={<InfoIcon />}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Business cash redemption requires a person authorized to withdraw credits (business owner or
            team member with <strong>Can Withdraw</strong>). That person&apos;s government ID must match the
            <strong> Team Member Profile display name</strong> shown below.
          </Typography>
          <Typography variant="body2">
            Your personal profile can stay private. This step only ties verified identity to the authorized
            team profile used for redemption.
          </Typography>
        </Alert>

        {error && <Alert severity="warning">{error}</Alert>}

        {alias ? (
          <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Your authorized team profile
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" fontWeight="medium">
                {displayName || alias.aliasUsername}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                @{alias.aliasUsername}
              </Typography>
              {alias.isOwner && <Chip size="small" label="Owner" color="primary" variant="outlined" />}
              {alias.canWithdraw && !alias.isOwner && (
                <Chip size="small" label="Can Withdraw" color="success" variant="outlined" />
              )}
              {nameBindingMet && alias.isBoundTarget && (
                <Chip
                  size="small"
                  icon={<CheckCircleIcon />}
                  label="Identity matched"
                  color="success"
                />
              )}
            </Box>
            {missingDisplayName && (
              <Alert severity="warning" sx={{ mt: 1.5 }}>
                Set a Team Member Profile display name that matches your government ID before verifying.
                Edit it on the{' '}
                <Link href="/profile?tab=additional-members" underline="hover">
                  Additional Members
                </Link>{' '}
                tab.
              </Alert>
            )}
            {!missingDisplayName && !nameBindingMet && (
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                Your ID must show exactly: <strong>{displayName}</strong>
              </Typography>
            )}
            {nameBindingMet && status.boundTeamDisplayName && (
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                Verified legal name: {status.boundTeamDisplayName}
              </Typography>
            )}
          </Box>
        ) : (
          <Alert severity="warning">
            No team member is yet authorized to manage the Cash Redemption Program. A business owner can
            assign authorized team members with the <strong>Can Withdraw</strong> setting on the{' '}
            <Link href="/profile?tab=additional-members" underline="hover">
              Additional Members
            </Link>{' '}
            tab.
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          <Button
            variant={isIdVerified ? 'outlined' : 'contained'}
            size="small"
            onClick={onVerifyIdentity}
            disabled={idVerificationStarting || missingDisplayName || !alias}
            startIcon={
              idVerificationStarting
                ? <CircularProgress size={14} color="inherit" />
                : <BadgeIcon />
            }
          >
            {idVerificationStarting
              ? 'Starting…'
              : (isIdVerified ? 'Re-verify Identity' : 'Verify Government ID')}
          </Button>
          <Button variant="text" size="small" onClick={load}>
            Refresh status
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default BusinessRedemptionIdentitySection;
